import { Suspense, lazy, useCallback, useState } from "react"

import {
    getPlaceholderImage,
    getRandomPlaceholderImage,
    onePhoto,
    otherPhotos,
} from "./data/photo-data"
import { Image } from "../Common/Image"
import {
    HERO_SIZES,
    HERO_WIDTHS,
    MOBILE_THUMB_SIZES,
    MOBILE_THUMB_WIDTHS,
} from "../../utils/imageUrl"

const MobilePhotoViewer = lazy(() => import("./MobilePhotoViewer"))

/**
 * How many thumbs the strip beneath the hero shows, and where they start.
 *
 * Photo 1 is the hero directly above the strip, so the strip runs from photo 2:
 * four slots showing four photos the visitor cannot already see beats four slots
 * where one repeats the image immediately above it. The overflow count on the
 * last thumb is therefore everything past photo 5.
 */
const STRIP_START = 1

const STRIP_COUNT = 4

export const MobileGallery = ({ item }) => {
    const [viewer, setViewer] = useState(null)

    const images = item.gun?.photo_array || []
    const hasPhotos = images.length > 0

    // Photo 1 leads. A listing with no servable photo renders its placeholder
    // through Image's fallback — seeded by the listing id so the server and
    // client renders agree — with nothing to enlarge.
    const heroSrc = hasPhotos ? images[0] : null
    const placeholder = getPlaceholderImage(item.id)

    const photo1 = onePhoto(item)
    const photoOthers = otherPhotos(item)

    const stripImages = images.slice(STRIP_START, STRIP_START + STRIP_COUNT)
    const overflowCount = images.length - (STRIP_START + STRIP_COUNT)

    const openViewer = useCallback((index, mode = "photo") => {
        setViewer({ index, mode })
    }, [])

    const closeViewer = useCallback(() => setViewer(null), [])

    return (
        <div className="lg:hidden">
            {/*
                Deliberately unlabelled: an aria-label here would override the
                button's contents, and the photo's own alt text is the only
                description of it on the mobile page. Named by its contents, the
                control announces the listing description followed by "Tap to
                enlarge", which carries both the subject and the action.
            */}
            <button
                type="button"
                className="relative block w-full text-left"
                disabled={!hasPhotos}
                onClick={() => openViewer(0)}
            >
                <Image
                    src={heroSrc}
                    alt={photo1}
                    className="w-full h-auto bg-gray-300"
                    width={576}
                    height={324}
                    fallbackSrc={placeholder}
                    data-photo={hasPhotos ? "listing" : "placeholder"}
                    // Same sizes/widths as PhotoGrid's hero and HeroPreload: this
                    // element is display:none on desktop but still eager-fetched, so
                    // matching them is what stops the LCP image downloading twice.
                    sizes={HERO_SIZES}
                    widths={HERO_WIDTHS}
                    eager
                    priority="high"
                    onError={(e) => {
                        // Idempotent: assigning src re-fires error when the
                        // placeholder fails too, so bail out once one is showing.
                        if (e.target.src.includes("/images/static/gun-skele")) {
                            return
                        }

                        e.target.srcset = ""
                        e.target.src = getRandomPlaceholderImage()
                    }}
                />

                {/*
                    Same treatment as the viewer's photo counter — white on
                    semi-transparent black. Deliberately not maroon (#820003),
                    which is reserved for primary CTAs like the action bar.
                */}
                {hasPhotos && (
                    <span className="absolute bottom-3 right-3 rounded bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
                        Tap to enlarge
                    </span>
                )}
            </button>

            {stripImages.length > 0 && (
                <ul className="flex gap-1.5 mt-1.5" aria-label="More photos">
                    {stripImages.map((image, index) => {
                        const photoIndex = STRIP_START + index
                        const isLast = index === stripImages.length - 1
                        const showOverflow = isLast && overflowCount > 0

                        return (
                            <li key={photoIndex} className="flex-1">
                                <button
                                    type="button"
                                    className="relative block w-full overflow-hidden rounded"
                                    onClick={() => openViewer(photoIndex)}
                                    aria-label={`View photo ${photoIndex + 1} of ${images.length}`}
                                >
                                    <Image
                                        src={image}
                                        alt={`${photoOthers} ${photoIndex + 1}`}
                                        className="object-cover w-full aspect-[4/3] bg-gray-300"
                                        width={120}
                                        height={90}
                                        widths={MOBILE_THUMB_WIDTHS}
                                        sizes={MOBILE_THUMB_SIZES}
                                        // Without this the `src` attribute is
                                        // the 1200px rendition — a full-size
                                        // photo URL in the initial HTML for a
                                        // 90px box, and four more
                                        // SERP-thumbnail candidates for Google
                                        // to choose between (FAL-199). Only
                                        // `src` is affected; the browser still
                                        // selects from `widths`.
                                        fallbackWidth={MOBILE_THUMB_WIDTHS[0]}
                                        priority="low"
                                        fallbackSrc={placeholder}
                                    />
                                    {showOverflow && (
                                        <span className="absolute inset-0 flex items-center justify-center text-base font-semibold text-white bg-black/60">
                                            +{overflowCount}
                                        </span>
                                    )}
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}

            {hasPhotos && (
                <button
                    type="button"
                    className="mt-2 mb-2 text-blue-500 cursor-pointer"
                    onClick={() => openViewer(0, "grid")}
                >
                    See more photos ({images.length})
                </button>
            )}

            {viewer && (
                <Suspense fallback={null}>
                    <MobilePhotoViewer
                        item={item}
                        images={images}
                        initialIndex={viewer.index}
                        initialMode={viewer.mode}
                        onClose={closeViewer}
                    />
                </Suspense>
            )}
        </div>
    )
}
